import { motion } from "framer-motion";
import { Heart, MessageCircle, Sparkles, Tag, Clock } from "lucide-react";
import { Reveal, SectionTitle } from "./Reveal";
import { PRICES, IMAGES, WHATSAPP } from "@/data/site";

const todayISO = () => new Date().toISOString().slice(0, 10);
const activeCampaign = (it) => it.campaign && it.campaign.until >= todayISO() ? it.campaign : null;
const fmtDate = (iso) => new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long" });

const PriceRow = ({ cat, it }) => {
  const c = activeCampaign(it);
  const slug = it.name.replace(/\s+/g, "-").toLowerCase();
  return (
    <li className={`group flex items-center gap-3 py-2.5 ${c ? "-mx-3 px-3 rounded-2xl bg-gradient-to-r from-[#fff0f5] to-transparent" : ""}`} data-testid={`price-item-${cat.id}-${slug}`}>
      <span className="text-sm sm:text-[15px] font-medium text-[#1a1118] flex items-center gap-2 flex-wrap">
        {it.name}
        {it.badge && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-grad-pink rounded-full px-2 py-0.5 shrink-0"><Sparkles size={10} />{it.badge}</span>}
        {c && (
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-white rounded-full px-2 py-0.5 shrink-0" style={{ background: "var(--grad-btn-dark)" }} data-testid={`campaign-badge-${slug}`}><Tag size={10} />Kampanya</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#8b004f] bg-white border border-pink-200 rounded-full px-2 py-0.5 shrink-0" data-testid={`campaign-until-${slug}`}><Clock size={10} />{fmtDate(c.until)}'a kadar</span>
          </span>
        )}
      </span>
      <span className="flex-1 min-w-[16px] border-b border-dotted border-pink-300/70 translate-y-1" />
      {c ? (
        <span className="shrink-0 flex items-center gap-2">
          <span className="text-xs text-zinc-400 line-through tabular-nums whitespace-nowrap" data-testid={`old-price-${slug}`}>{it.price}</span>
          <span className="whitespace-nowrap rounded-xl text-white px-3.5 py-1.5 text-sm font-bold tabular-nums shadow-[0_8px_20px_-8px_rgba(230,0,126,0.6)] transition-transform duration-300 group-hover:scale-105" style={{ background: "var(--grad-btn-dark)" }} data-testid={`new-price-${slug}`}>{c.newPrice}</span>
        </span>
      ) : (
        <span className="shrink-0 whitespace-nowrap rounded-xl bg-[#fff0f5] px-3.5 py-1.5 text-sm font-bold text-gradient tabular-nums transition-transform duration-300 group-hover:scale-105">{it.price}</span>
      )}
    </li>
  );
};

const PriceCard = ({ cat, i }) => (
  <motion.div
    data-testid={`price-card-${cat.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
    className="gradient-border rounded-[2rem] bg-white/90 backdrop-blur-xl shadow-[0_30px_70px_-35px_rgba(139,0,79,0.35)] overflow-hidden flex flex-col"
  >
    <div className="relative h-44 overflow-hidden">
      <img src={IMAGES[cat.imageKey]} alt={`Esenyurt ${cat.title} fiyatları – Diva Nail Beauty`} loading="lazy" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
      <div className="absolute bottom-4 left-6 right-6 flex items-center gap-3">
        <h3 className="text-2xl font-bold tracking-tight text-[#8b004f]">{cat.title}</h3>
        <Heart size={18} className="text-[#e6007e] fill-[#ffd1dc]" />
      </div>
    </div>
    <ul className="px-6 pb-7 pt-2 space-y-1 flex-1">
      {cat.items.map((it) => <PriceRow key={it.name} cat={cat} it={it} />)}
    </ul>
  </motion.div>
);

export const Prices = () => (
  <section id="fiyatlar" className="relative py-28 lg:py-36 bg-white overflow-hidden" data-testid="prices-section">
    <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[radial-gradient(ellipse,rgba(255,209,220,0.45),transparent_65%)] blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <SectionTitle eyebrow="Fiyat Listesi" title={<>Şeffaf ve <span className="text-gradient">net fiyatlar</span></>} desc="Tüm hizmetlerimizde kaliteli ürün, hijyenik uygulama ve profesyonel dokunuş fiyata dahildir." />
        <Reveal delay={0.2}>
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="prices-whatsapp-button" className="btn-grad-dark inline-flex items-center gap-2 text-white font-semibold px-7 py-4 rounded-full"><MessageCircle size={18} /> WhatsApp'tan Randevu Al</a>
        </Reveal>
      </div>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PRICES.map((c, i) => <PriceCard key={c.id} cat={c} i={i} />)}
      </div>
      <Reveal delay={0.2} className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs text-zinc-400">Fiyatlar bilgilendirme amaçlıdır; kampanyalı fiyatlar belirtilen tarihe kadar geçerlidir.</p>
        <p className="text-lg sm:text-xl italic font-medium text-gradient" style={{ fontFamily: "Poppins" }}>Kendine iyi bak, çünkü sen bir Divasın ♡</p>
      </Reveal>
    </div>
  </section>
);
